import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  FaArrowLeft, 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaEye,
  FaSearch,
  FaBlog
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import api from '../../config/api';

const ManageBlogs = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [admin, setAdmin] = useState(null);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const adminData = localStorage.getItem('adminUser');
    if (adminData) {
      setAdmin(JSON.parse(adminData));
    }
    fetchBlogs();
  }, []);

  const filterBlogItems = useCallback(() => {
    let filtered = [...blogs];

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.author?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by category
    if (filterCategory !== 'all') {
      filtered = filtered.filter(item => item.category === filterCategory);
    }

    setFilteredBlogs(filtered);
  }, [searchTerm, filterCategory, blogs]);

  useEffect(() => {
    filterBlogItems();
  }, [filterBlogItems]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/blog');
      console.log('Fetched blogs:', response.data);
      setBlogs(response.data || []);
      setFilteredBlogs(response.data || []);
      
      // Extract unique categories
      const uniqueCategories = [...new Set(response.data.map(b => b.category).filter(Boolean))];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Error fetching blogs:', error);
      alert('Failed to fetch blogs');
      setBlogs([]);
      setFilteredBlogs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) {
      return;
    }

    try {
      const response = await api.delete(`/blog/${id}`);
      if (response.data.success) {
        alert('Blog deleted successfully!');
        fetchBlogs();
      }
    } catch (error) {
      console.error('Error deleting blog:', error);
      alert('Failed to delete blog');
    }
  };

  // Format tags - FIXED VERSION
  const formatTags = (tags) => {
    // If tags is already an array
    if (Array.isArray(tags)) {
      return tags.map(tag => typeof tag === 'object' ? tag.name : tag);
    }
    
    // If tags is a string
    if (typeof tags === 'string' && tags.trim() !== '') {
      return tags.split(',').map(tag => tag.trim());
    }
    
    // If tags is empty or invalid
    return [];
  };

  const canDelete = admin?.role === 'super_admin' || admin?.role === 'admin';

  const BlogCard = ({ item }) => {
    const tagsArray = formatTags(item.tags);
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all"
      >
        {item.image && (
          <img
            src={item.image.startsWith('http') ? item.image : `https://worganaturals.com${item.image}`}
            alt={item.image_alt || item.title}
            className="w-full h-48 object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        )}
        <div className="p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              {item.category && (
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                  {item.emoji} {item.category}
                </span>
              )}
              {item.featured === 1 && (
                <span className="px-2 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                  ⭐ Featured
                </span>
              )}
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {item.created_at ? new Date(item.created_at).toLocaleDateString() : 'N/A'}
            </span>
          </div>
          
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 line-clamp-2">
            {item.title}
          </h3>
          
          <p className="text-gray-600 dark:text-gray-400 mb-4 line-clamp-3">
            {item.excerpt || (item.content ? item.content.substring(0, 150) + '...' : '')}
          </p>

          {item.author && (
            <div className="mb-4">
              <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                {item.author}
              </p>
              {item.author_role && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {item.author_role}
                </p>
              )}
            </div>
          )}

          {tagsArray.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {tagsArray.slice(0, 3).map((tag, index) => (
                <span
                  key={index}
                  className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded"
                >
                  #{tag}
                </span>
              ))}
              {tagsArray.length > 3 && (
                <span className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                  +{tagsArray.length - 3} more
                </span>
              )}
            </div>
          )}

          {/* Status & Stats */}
          <div className="flex items-center justify-between mb-4 text-xs text-gray-500 dark:text-gray-400">
            <span className={`px-2 py-1 rounded ${
              item.status === 'published' 
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                : item.status === 'draft'
                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
            }`}>
              {item.status || 'draft'}
            </span>
            <div className="flex items-center space-x-3">
              {item.read_time && (
                <span>📖 {item.read_time}</span>
              )}
              <span className="flex items-center">
                <FaEye className="mr-1" />
                {item.views || 0}
              </span>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => navigate(`/admin/blog/edit/${item.id}`)}
              className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all"
            >
              <FaEdit />
              <span>Edit</span>
            </button>
            
            <button
              onClick={() => window.open(`/blog/${item.slug}`, '_blank')}
              className="flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all"
              title="View Post"
            >
              <FaEye />
            </button>
            
            {canDelete && (
              <button
                onClick={() => handleDelete(item.id)}
                className="flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all"
                title="Delete Post"
              >
                <FaTrash />
              </button>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading blogs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all"
              >
                <FaArrowLeft className="text-gray-600 dark:text-gray-400" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Manage Blogs
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {filteredBlogs.length} post{filteredBlogs.length !== 1 ? 's' : ''} found
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/admin/blog/create')}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all"
            >
              <FaPlus />
              <span>Add Blog</span>
            </button>
          </div>
        </div>
      </header>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search blogs by title, content, or author..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Category Filter */}
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Blogs Grid */}
        {filteredBlogs.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBlogs.map(item => (
              <BlogCard key={item.id} item={item} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <FaBlog className="text-6xl text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              No blogs found
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {searchTerm || filterCategory !== 'all'
                ? 'Try adjusting your filters'
                : 'Start by creating your first blog post'}
            </p>
            <button
              onClick={() => navigate('/admin/blog/create')}
              className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all inline-flex items-center space-x-2"
            >
              <FaPlus />
              <span>Add Blog</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageBlogs;
